import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Building2, Users, BookOpen, FileText, ArrowRight, UserMinus, GraduationCap,
  Layers, MapPin, UserCog, CalendarDays, Save,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

const ROLE_TITLE = {
  superadmin: "Panel del Superadministrador",
  jefe_sector: "Panel del Jefe de Sector",
  supervisor_zona: "Panel del Supervisor de Zona",
  director: "Panel del Director(a)",
  docente: "Panel del Docente",
};

const ROLE_SUBTITLE = {
  superadmin: "Vista integral de sectores, zonas y planteles del sistema.",
  jefe_sector: "Zonas escolares y planteles de sus sectores asignados.",
  supervisor_zona: "Planteles y directores de sus zonas escolares.",
  director: "Grupos, docentes y matrícula de sus planteles.",
  docente: "Sus grupos y alumnos asignados.",
};

const CARDS_BY_ROLE = {
  superadmin: [
    { key: "sectores", label: "Sectores", icon: Layers, to: "/admin/sectores", color: "#10312B", source: "context" },
    { key: "zonas", label: "Zonas escolares", icon: MapPin, to: "/admin/zonas", color: "#235B4E", source: "context" },
    { key: "planteles", label: "Planteles", icon: Building2, to: "/admin/planteles", color: "#BC955C", source: "totales" },
    { key: "supervisores", label: "Supervisores", icon: UserCog, to: "/admin/usuarios", color: "#9F2241", source: "context" },
    { key: "alumnos_activos", label: "Alumnos activos", icon: GraduationCap, to: "/admin/reportes", color: "#154360", source: "totales" },
  ],
  jefe_sector: [
    { key: "sectores", label: "Mis sectores", icon: Layers, to: "/admin/sectores", color: "#10312B", source: "context" },
    { key: "zonas", label: "Zonas escolares", icon: MapPin, to: "/admin/zonas", color: "#235B4E", source: "context" },
    { key: "supervisores", label: "Supervisores", icon: UserCog, to: "/admin/usuarios", color: "#9F2241", source: "context" },
    { key: "planteles", label: "Planteles", icon: Building2, to: "/admin/planteles", color: "#BC955C", source: "totales" },
  ],
  supervisor_zona: [
    { key: "zonas", label: "Mis zonas", icon: MapPin, to: "/admin/zonas", color: "#235B4E", source: "context" },
    { key: "directores", label: "Directores", icon: UserCog, to: "/admin/usuarios", color: "#9F2241", source: "context" },
    { key: "planteles", label: "Planteles", icon: Building2, to: "/admin/planteles", color: "#BC955C", source: "totales" },
  ],
  director: [
    { key: "planteles", label: "Mis planteles", icon: Building2, to: "/admin/planteles", color: "#10312B", source: "context" },
    { key: "docentes", label: "Docentes", icon: Users, to: "/admin/usuarios", color: "#9F2241", source: "totales" },
    { key: "grupos", label: "Grupos", icon: BookOpen, to: "/admin/grupos", color: "#BC955C", source: "totales" },
    { key: "alumnos_activos", label: "Alumnos", icon: GraduationCap, to: "/admin/alumnos-lista", color: "#154360", source: "totales" },
    { key: "alumnos_baja", label: "Bajas", icon: UserMinus, to: "/admin/bajas", color: "#6B7280", source: "totales" },
  ],
};

const BREAKDOWN_CONFIG = {
  superadmin: {
    heading: "Distribución por sector",
    columns: [
      { key: "titulo", label: "Sector", className: "font-medium text-[#10312B]" },
      { key: "subtitulo", label: "Nombre", className: "text-[#545454]" },
      { key: "zonas", label: "Zonas", numeric: true },
      { key: "planteles", label: "Planteles", numeric: true },
      { key: "alumnos", label: "Alumnos", numeric: true },
    ],
    empty: "Sin sectores registrados",
  },
  jefe_sector: {
    heading: "Sus sectores",
    columns: [
      { key: "titulo", label: "Sector", className: "font-medium text-[#10312B]" },
      { key: "subtitulo", label: "Nombre", className: "text-[#545454]" },
      { key: "zonas", label: "Zonas", numeric: true },
      { key: "planteles", label: "Planteles", numeric: true },
      { key: "alumnos", label: "Alumnos", numeric: true },
    ],
    empty: "No tiene sectores asignados. Contacte al superadministrador.",
  },
  supervisor_zona: {
    heading: "Sus zonas escolares",
    columns: [
      { key: "titulo", label: "Zona", className: "font-medium text-[#10312B]" },
      { key: "subtitulo", label: "Nivel" },
      { key: "planteles", label: "Planteles", numeric: true },
      { key: "alumnos", label: "Alumnos", numeric: true },
    ],
    empty: "No tiene zonas asignadas. Contacte a su jefe de sector.",
  },
  director: {
    heading: "Sus planteles",
    columns: [
      { key: "titulo", label: "Plantel", className: "font-medium text-[#10312B]" },
      { key: "subtitulo", label: "CCT · Nivel" },
      { key: "grupos", label: "Grupos", numeric: true },
      { key: "alumnos", label: "Alumnos", numeric: true },
    ],
    empty: "No tiene planteles asignados aún.",
  },
};

const QUICK_ACTIONS = {
  superadmin: [
    { title: "Gestionar sectores", desc: "Alta, baja y edición de sectores educativos.", to: "/admin/sectores" },
    { title: "Nuevo usuario", desc: "Cree cuentas de Jefe, Supervisor, Director o Docente.", to: "/admin/usuarios" },
    { title: "Generar reporte 911", desc: "Formato oficial listo para exportar.", to: "/admin/reportes" },
  ],
  jefe_sector: [
    { title: "Alta de supervisor", desc: "Cree supervisores de zona y asigne zonas.", to: "/admin/usuarios" },
    { title: "Nueva zona escolar", desc: "Registrar una zona en un sector.", to: "/admin/zonas" },
    { title: "Ver reportes 911", desc: "Consolidado de sus sectores.", to: "/admin/reportes" },
  ],
  supervisor_zona: [
    { title: "Alta de director", desc: "Registre directores y asigne planteles.", to: "/admin/usuarios" },
    { title: "Ver planteles", desc: "Planteles de sus zonas asignadas.", to: "/admin/planteles" },
    { title: "Reportes 911", desc: "Consolidado de sus zonas.", to: "/admin/reportes" },
  ],
  director: [
    { title: "Alta de docente", desc: "Cree docentes y asígnelos a planteles.", to: "/admin/usuarios" },
    { title: "Crear grupo", desc: "Alta de grupo con grado y docente.", to: "/admin/grupos" },
    { title: "Reporte 911 del plantel", desc: "Formato oficial listo para entregar.", to: "/admin/reportes" },
  ],
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/estadisticas/global");
        setStats(data);
      } catch (err) { console.error(err); }
    })();
  }, []);

  const role = user?.role || "superadmin";
  const cards = CARDS_BY_ROLE[role] || CARDS_BY_ROLE.superadmin;
  const breakdown = BREAKDOWN_CONFIG[role] || BREAKDOWN_CONFIG.superadmin;
  const actions = QUICK_ACTIONS[role] || QUICK_ACTIONS.superadmin;

  const cardValue = (c) => {
    const src = c.source === "context" ? stats?.context_counts : stats?.totales;
    return src?.[c.key] ?? 0;
  };

  const chartData = stats?.por_grado || [];
  const rows = stats?.breakdown || [];

  return (
    <AppLayout>
      <div className="max-w-6xl">
        <div className="mb-8">
          <div className="text-xs tracking-[0.2em] uppercase text-[#9F2241] mb-2">
            {stats?.role_label || user?.role_label || "Panel"}
          </div>
          <h1 className="text-3xl md:text-4xl font-semibold text-[#10312B] tracking-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            {ROLE_TITLE[role]}
          </h1>
          <p className="text-sm text-[#545454] mt-2 max-w-2xl">
            {ROLE_SUBTITLE[role]}
          </p>
        </div>

        <div className={`grid gap-4 mb-8 ${cards.length <= 3 ? "grid-cols-1 sm:grid-cols-3" : cards.length === 4 ? "grid-cols-2 lg:grid-cols-4" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5"}`}>
          {cards.map((c) => (
            <Link key={c.key} to={c.to} data-testid={`stat-${c.key}`}>
              <Card className="border-[#E5E7EB] hover:border-[#235B4E] transition-colors duration-200 cursor-pointer h-full">
                <CardContent className={cards.length <= 3 ? "p-7" : "p-5"}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-[#9CA3AF]">{c.label}</div>
                      <div className={`${cards.length <= 3 ? "text-5xl" : "text-3xl"} font-semibold mt-2`} style={{ color: c.color, fontFamily: "'Montserrat', sans-serif" }}>
                        {cardValue(c)}
                      </div>
                    </div>
                    <div className={`${cards.length <= 3 ? "w-12 h-12" : "w-9 h-9"} rounded flex items-center justify-center`} style={{ backgroundColor: `${c.color}15` }}>
                      <c.icon className={`${cards.length <= 3 ? "w-6 h-6" : "w-5 h-5"}`} style={{ color: c.color }} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        <FechaCorteCard role={role} />

        {role !== "supervisor_zona" && (
        <Card className="border-[#E5E7EB] mb-8" data-testid="breakdown-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#9F2241] mb-1">Desglose</div>
                <h2 className="text-lg font-semibold text-[#10312B]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  {breakdown.heading}
                </h2>
              </div>
              <div className="text-xs text-[#545454]">{rows.length} registro{rows.length === 1 ? "" : "s"}</div>
            </div>
            {rows.length === 0 ? (
              <div className="py-10 text-sm text-[#9CA3AF] text-center">{breakdown.empty}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#E5E7EB] text-[11px] uppercase tracking-wider text-[#6B7280]">
                      {breakdown.columns.map((col) => (
                        <th key={col.key} className={`py-2 ${col.numeric ? "text-right" : "text-left"}`}>{col.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]">
                        {breakdown.columns.map((col) => (
                          <td key={col.key} className={`py-2 ${col.numeric ? "text-right font-mono" : ""} ${col.className || ""}`}>
                            {r[col.key] ?? "—"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {role !== "supervisor_zona" && (
        <Card className="border-[#E5E7EB] mb-8">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#9F2241] mb-1">Estadísticas en vivo</div>
                <h2 className="text-lg font-semibold text-[#10312B]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Matrícula por grado y sexo
                </h2>
              </div>
              <div className="text-xs text-[#545454] flex items-center gap-4">
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm bg-[#235B4E]" /> Hombres
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm bg-[#9F2241]" /> Mujeres
                </span>
              </div>
            </div>
            <div className="w-full" style={{ height: 320 }} data-testid="chart-matricula">
              {chartData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-[#9CA3AF]">
                  Aún no hay alumnos registrados para visualizar
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#545454" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#545454" }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ border: "1px solid #235B4E", borderRadius: 4, fontSize: 12 }}
                      cursor={{ fill: "#235B4E10" }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                    <Bar dataKey="H" name="Hombres" fill="#235B4E" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="M" name="Mujeres" fill="#9F2241" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>
        )}

        {role !== "supervisor_zona" && (
        <div className="grid md:grid-cols-3 gap-4">
          {actions.map((q) => (
            <Link key={q.title} to={q.to} className="group">
              <Card className="border-[#E5E7EB] group-hover:border-[#235B4E] transition-colors">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-[#10312B]">{q.title}</h3>
                    <ArrowRight className="w-4 h-4 text-[#9CA3AF] group-hover:text-[#235B4E] transition-colors" />
                  </div>
                  <p className="text-sm text-[#545454]">{q.desc}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
        )}
      </div>
    </AppLayout>
  );
}

function FechaCorteCard({ role }) {
  const { configGlobal, refreshConfigGlobal } = useAuth();
  const isSuper = role === "superadmin";
  const [pre, setPre] = useState(configGlobal.fecha_corte_preescolar);
  const [pri, setPri] = useState(configGlobal.fecha_corte_primaria);
  const [sec, setSec] = useState(configGlobal.fecha_corte_secundaria);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPre(configGlobal.fecha_corte_preescolar);
    setPri(configGlobal.fecha_corte_primaria);
    setSec(configGlobal.fecha_corte_secundaria);
  }, [configGlobal.fecha_corte_preescolar, configGlobal.fecha_corte_primaria, configGlobal.fecha_corte_secundaria]);

  const nivelesRows = [
    // NOTA: preescolar y secundaria ocultos temporalmente — solo Primaria activa.
    // { key: "preescolar", label: "Preescolar", value: pre, set: setPre, cur: configGlobal.fecha_corte_preescolar },
    { key: "primaria",   label: "Primaria",   value: pri, set: setPri, cur: configGlobal.fecha_corte_primaria },
    // { key: "secundaria", label: "Secundaria", value: sec, set: setSec, cur: configGlobal.fecha_corte_secundaria },
  ];

  const dirty =
    pre !== configGlobal.fecha_corte_preescolar ||
    pri !== configGlobal.fecha_corte_primaria ||
    sec !== configGlobal.fecha_corte_secundaria;

  const preset = (row, monthDay) => {
    const y = (row.value || row.cur || "2025-09-01").slice(0, 4);
    row.set(`${y}-${monthDay}`);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/config/global", {
        fecha_corte_preescolar: pre,
        fecha_corte_primaria:   pri,
        fecha_corte_secundaria: sec,
      });
      await refreshConfigGlobal();
      toast.success("Fecha de corte actualizada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  return (
    <Card className="border-[#E5E7EB] mb-8" data-testid="fecha-corte-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#9F2241] mb-1 flex items-center gap-2">
              <CalendarDays className="w-3 h-3" /> Fecha de corte · edad de alumnos
            </div>
            <h2 className="text-lg font-semibold text-[#10312B]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {isSuper
                ? "Configura la fecha por nivel para calcular años cumplidos"
                : "Fecha vigente para el cálculo de edad por nivel"}
            </h2>
            <p className="text-xs text-[#545454] mt-1">
              La edad de cada alumno se calcula como años cumplidos a esta fecha.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-1 gap-4 max-w-md">
          {nivelesRows.map((r) => (
            <div key={r.key} className="p-3 bg-[#F9FAFB] rounded border border-[#F3F4F6]">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[11px] uppercase tracking-wider text-[#6B7280]">{r.label}</Label>
                <span className="text-[10px] font-mono text-[#9CA3AF]">actual: {r.cur}</span>
              </div>
              {isSuper ? (
                <>
                  <Input
                    type="date"
                    value={r.value || ""}
                    onChange={(e) => r.set(e.target.value)}
                    data-testid={`corte-${r.key}`}
                    className="mb-2"
                  />
                  <div className="flex gap-1">
                    <Button
                      type="button" variant="outline" size="sm"
                      className="text-[10px] h-6 px-2"
                      data-testid={`corte-${r.key}-01sep`}
                      onClick={() => preset(r, "09-01")}
                    >
                      1° sep
                    </Button>
                    <Button
                      type="button" variant="outline" size="sm"
                      className="text-[10px] h-6 px-2"
                      data-testid={`corte-${r.key}-30sep`}
                      onClick={() => preset(r, "09-30")}
                    >
                      30 sep
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-lg font-semibold text-[#10312B] font-mono">{r.cur}</div>
              )}
            </div>
          ))}
        </div>

        {isSuper && (
          <div className="mt-4 flex items-center justify-end gap-2">
            {dirty && (
              <span className="text-xs text-[#9F2241]">Hay cambios sin guardar</span>
            )}
            <Button
              onClick={save}
              disabled={!dirty || saving}
              data-testid="corte-save"
              className="bg-[#235B4E] hover:bg-[#10312B] text-white"
            >
              <Save className="w-4 h-4 mr-1.5" />
              {saving ? "Guardando…" : "Guardar cambios"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

